import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLanguage } from '../contexts/LanguageContext'

export interface SelectOption {
  value: string
  label: string
  /** ラベルの左に添える絵文字。省略可 */
  icon?: string
  /** 見出しでまとめたいときのグループ名。同じ名前が続く並びで1つの見出しになる */
  group?: string
  /** ラベル以外にも検索で引っかけたい文字列（種別名やかな表記など） */
  keywords?: string
}

/** ポップアップを画面座標で置くための位置情報 */
interface Position {
  left: number
  width: number
  /** 下向きに開くときの位置。上向きのときは bottom を使う */
  top?: number
  bottom?: number
  maxHeight: number
}

interface BaseProps {
  options: SelectOption[]
  /** 値が空のときにトリガーへ出す文言。空の選択肢を出したい場合は options の先頭に value: '' を入れる */
  placeholder?: string
  /** 検索欄のプレースホルダ。省略すると汎用の「検索」になる */
  searchPlaceholder?: string
  /** トリガーに適用するクラス。置き換え前の select と同じものを渡す */
  className?: string
  disabled?: boolean
  'data-testid'?: string
}

interface SingleProps extends BaseProps {
  multiple?: false
  value: string
  onChange: (value: string) => void
}

interface MultipleProps extends BaseProps {
  /**
   * 複数選択モード。選択はチェックで示し、選んでもポップアップは開いたままにする。
   * 空配列は「すべて」（絞り込まない）を表し、options に value: '' を入れておくと
   * その項目がまとめて解除するボタンになる。
   */
  multiple: true
  value: string[]
  onChange: (value: string[]) => void
}

type Props = SingleProps | MultipleProps

/**
 * select の代わりに使うコンボボックス。
 * 見た目は select に寄せつつ、開いたときに検索欄で項目を絞り込める。
 * カテゴリ・口座など候補が多い選択肢はこれで統一する。
 */
export function SearchableSelect(props: Props) {
  const {
    options,
    placeholder,
    searchPlaceholder,
    className = '',
    disabled,
    'data-testid': testId,
  } = props
  const isMultiple = props.multiple === true
  // 単一選択も配列に寄せて、以降の判定を一本化する
  const selectedValues: string[] = props.multiple
    ? props.value
    : props.value ? [props.value] : []
  const { t } = useLanguage()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<Position | null>(null)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) =>
      `${o.label} ${o.keywords ?? ''}`.toLowerCase().includes(q),
    )
  }, [options, query])

  // アイコンを持つ項目が1つでもあれば、無い項目にも幅を空けてラベルの左端を揃える
  const hasIcon = options.some((o) => o.icon)

  // 選択済みの項目。複数選択で何も選ばれていなければ、value: '' の「すべて」を出す
  const selectedOptions = selectedValues.length
    ? options.filter((o) => o.value !== '' && selectedValues.includes(o.value))
    : options.filter((o) => o.value === '')
  const selected = selectedOptions[0]
  const selectedLabel = selectedOptions.length > 1
    ? t('select_multi_summary')
        .replace('{label}', selectedOptions[0].label)
        .replace('{count}', String(selectedOptions.length - 1))
    : selected?.label ?? ''
  const searchLabel = searchPlaceholder ?? t('select_search_placeholder')

  // トリガーの位置から、ポップアップを置く画面座標と入る高さを決める
  const updatePosition = useCallback(() => {
    const trigger = containerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const gap = 4
    const margin = 8
    const below = window.innerHeight - rect.bottom - gap - margin
    const above = rect.top - gap - margin
    // 下に十分な高さが無く、上のほうが広いなら上向きに開く
    const openUp = below < 240 && above > below
    const width = Math.max(rect.width, 192)
    setPosition({
      left: Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin)),
      width,
      ...(openUp ? { bottom: window.innerHeight - rect.top + gap } : { top: rect.bottom + gap }),
      // 画面いっぱいに伸びると読みにくいので、収まる高さの範囲で上限を設ける
      maxHeight: Math.max(160, Math.min(320, Math.floor(openUp ? above : below))),
    })
  }, [])

  // モーダルの中で開くと親の overflow に切られてしまうので、body 直下に画面座標で出す。
  // 開いている間はスクロールやリサイズに追従させる
  useEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }
    updatePosition()
    const handle = () => updatePosition()
    window.addEventListener('resize', handle)
    // 祖先の要素のスクロールも拾いたいのでキャプチャで受ける
    window.addEventListener('scroll', handle, true)
    return () => {
      window.removeEventListener('resize', handle)
      window.removeEventListener('scroll', handle, true)
    }
  }, [open, updatePosition])

  // 開いたら検索欄を空にして、いま選ばれている項目にカーソルを合わせる
  useEffect(() => {
    if (!open) return
    setQuery('')
    const index = filtered.findIndex((o) => selectedValues.includes(o.value))
    setActiveIndex(index >= 0 ? index : 0)
    // 開いた瞬間だけ実行したいので filtered / value は依存に入れない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // 無効化されたまま開きっぱなしにならないようにする
  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  // ポップアップは位置が決まってから body に出るので、open になった直後の
  // effect ではまだ検索欄が DOM に無い。マウントされた時点でフォーカスを当て、
  // プルダウンを開いたらそのまま入力できるようにする
  const focusSearchInput = useCallback((el: HTMLInputElement | null) => {
    el?.focus()
  }, [])

  // カーソル位置がリストの外に出ないよう追従させる
  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, open])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      // ポップアップは body 直下に出ているので、トリガーとは別に判定する
      if (containerRef.current?.contains(target) || popupRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const select = (next: string) => {
    if (props.multiple) {
      // 空の選択肢は「すべて」なので、押されたら条件をまるごと外して閉じる
      if (!next) {
        props.onChange([])
        setOpen(false)
        return
      }
      // 続けて選べるよう、複数選択では閉じない
      props.onChange(
        props.value.includes(next)
          ? props.value.filter((v) => v !== next)
          : [...props.value, next],
      )
      return
    }
    props.onChange(next)
    setOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (filtered.length === 0 ? 0 : (i + 1) % filtered.length))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => (filtered.length === 0 ? 0 : (i - 1 + filtered.length) % filtered.length))
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const option = filtered[activeIndex]
      if (option) select(option.value)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        data-testid={testId}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault()
            setOpen(true)
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex items-center justify-between gap-2 text-left disabled:opacity-50 ${className}`}
      >
        <span className={`flex min-w-0 items-center gap-1.5 ${selectedLabel ? '' : 'text-gray-400 dark:text-gray-500'}`}>
          {selectedOptions.length === 1 && selected?.icon && <span aria-hidden className="shrink-0">{selected.icon}</span>}
          <span className="truncate">{selectedLabel || placeholder || searchLabel}</span>
        </span>
        <span aria-hidden className="shrink-0 text-gray-400 dark:text-gray-500">▾</span>
      </button>
      {open && position && createPortal(
        <div
          ref={popupRef}
          style={{
            left: position.left,
            width: position.width,
            top: position.top,
            bottom: position.bottom,
            maxHeight: position.maxHeight,
          }}
          className="fixed z-[60] flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800"
        >
          <input
            ref={focusSearchInput}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setActiveIndex(0)
            }}
            onKeyDown={handleKeyDown}
            placeholder={searchLabel}
            autoComplete="off"
            className="w-full shrink-0 border-b border-gray-200 bg-transparent px-3 py-2 text-sm focus:outline-none dark:border-gray-600 dark:text-gray-100"
          />
          <div ref={listRef} role="listbox" aria-multiselectable={isMultiple || undefined} className="flex-1 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">{t('select_no_result')}</div>
            )}
            {filtered.map((o, i) => {
              // 「すべて」（value: ''）は、複数選択では何も選んでいない状態がチェック済みにあたる
              const isSelected = o.value
                ? selectedValues.includes(o.value)
                : selectedValues.length === 0
              return (
              <div key={o.value || '__empty__'}>
                {/* 絞り込みで並びが変わっても見出しが正しく付くよう、直前の項目と比べて出し分ける */}
                {o.group && o.group !== filtered[i - 1]?.group && (
                  <div className="px-3 pt-2 pb-1 text-xs font-medium text-gray-400 dark:text-gray-500">{o.group}</div>
                )}
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  data-index={i}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    select(o.value)
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                    i === activeIndex
                      ? 'bg-indigo-50 text-gray-900 dark:bg-indigo-950 dark:text-gray-100'
                      : 'text-gray-800 dark:text-gray-200'
                  } ${isSelected ? 'font-medium' : ''}`}
                >
                  {isMultiple && (
                    <span
                      aria-hidden
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] leading-none ${
                        isSelected
                          ? 'border-indigo-600 bg-indigo-600 text-white'
                          : 'border-gray-300 dark:border-gray-500'
                      }`}
                    >
                      {isSelected ? '✓' : ''}
                    </span>
                  )}
                  {hasIcon && <span aria-hidden className="w-5 shrink-0 text-center">{o.icon ?? ''}</span>}
                  <span className="truncate">{o.label}</span>
                </button>
              </div>
              )
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
